import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Image from "@tiptap/extension-image";
import { useEffect, useCallback } from "react";
import {
  Bold,
  Italic,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  Link as LinkIcon,
  Image as ImageIcon,
  Undo2,
  Redo2,
  Minus,
} from "lucide-react";

function ToolbarButton({ onClick, active, disabled, label, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-pressed={active}
      title={label}
      data-testid={testId}
      className={`h-8 w-8 flex items-center justify-center border transition-colors disabled:opacity-40 ${
        active
          ? "bg-ink text-paper border-ink"
          : "bg-transparent text-ink border-[hsl(var(--line-strong))] hover:border-ink"
      }`}
    >
      {children}
    </button>
  );
}

export default function RichTextEditor({ value, onChange, placeholder }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        codeBlock: { HTMLAttributes: { class: "language-plain" } },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noopener nofollow", target: "_blank" },
      }),
      Placeholder.configure({
        placeholder:
          placeholder ||
          "Start writing… (use the toolbar for headings, lists, links, quotes)",
      }),
      Image.configure({
        HTMLAttributes: { class: "editor-image" },
        allowBase64: false,
      }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class:
          "prose-redkey min-h-[400px] focus:outline-none px-4 py-4 max-w-none",
        "data-testid": "editor-body-tiptap",
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      // Return empty string when nothing but the default empty <p></p> exists
      onChange(html === "<p></p>" ? "" : html);
    },
  });

  // Sync external value changes (e.g. loading a post) without breaking cursor
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || "";
    if (current !== next && next !== "") {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [value, editor]);

  const promptLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL (leave empty to remove):", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    // Normalise
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
  }, [editor]);

  const promptImage = useCallback(() => {
    if (!editor) return;
    const url = window.prompt("Image URL:");
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
  }, [editor]);

  if (!editor) {
    return (
      <div
        className="border hairline-strong bg-paper-alt p-4 text-ink-soft"
        data-testid="rich-editor-loading"
      >
        Loading editor…
      </div>
    );
  }

  return (
    <div
      className="border hairline-strong bg-paper focus-within:border-ink"
      data-testid="rich-editor"
    >
      <div
        className="flex flex-wrap gap-1 p-2 border-b hairline bg-paper-alt sticky top-0 z-10"
        role="toolbar"
        aria-label="Formatting"
      >
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          label="Bold"
          testId="editor-btn-bold"
        >
          <Bold size={14} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          label="Italic"
          testId="editor-btn-italic"
        >
          <Italic size={14} />
        </ToolbarButton>
        <span className="mx-1 w-px bg-[hsl(var(--line-strong))]" />
        <ToolbarButton
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          active={editor.isActive("heading", { level: 2 })}
          label="Section heading (H2)"
          testId="editor-btn-h2"
        >
          <Heading2 size={14} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
          active={editor.isActive("heading", { level: 3 })}
          label="Subheading (H3)"
          testId="editor-btn-h3"
        >
          <Heading3 size={14} />
        </ToolbarButton>
        <span className="mx-1 w-px bg-[hsl(var(--line-strong))]" />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive("bulletList")}
          label="Bulleted list"
          testId="editor-btn-ul"
        >
          <List size={14} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive("orderedList")}
          label="Numbered list"
          testId="editor-btn-ol"
        >
          <ListOrdered size={14} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive("blockquote")}
          label="Quote"
          testId="editor-btn-quote"
        >
          <Quote size={14} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          active={editor.isActive("codeBlock")}
          label="Code block"
          testId="editor-btn-code"
        >
          <Code size={14} />
        </ToolbarButton>
        <span className="mx-1 w-px bg-[hsl(var(--line-strong))]" />
        <ToolbarButton
          onClick={promptLink}
          active={editor.isActive("link")}
          label="Link"
          testId="editor-btn-link"
        >
          <LinkIcon size={14} />
        </ToolbarButton>
        <ToolbarButton
          onClick={promptImage}
          label="Insert image (URL)"
          testId="editor-btn-image"
        >
          <ImageIcon size={14} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          label="Horizontal rule"
          testId="editor-btn-hr"
        >
          <Minus size={14} />
        </ToolbarButton>
        <span className="mx-1 w-px bg-[hsl(var(--line-strong))]" />
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().chain().focus().undo().run()}
          label="Undo"
          testId="editor-btn-undo"
        >
          <Undo2 size={14} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().chain().focus().redo().run()}
          label="Redo"
          testId="editor-btn-redo"
        >
          <Redo2 size={14} />
        </ToolbarButton>
      </div>

      <EditorContent editor={editor} />
    </div>
  );
}
